import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { api } from "@/helper/api";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";

export type MoveTarget = (
  | { kind: "singleUser"; user: any }
  | { kind: "selectedUsers"; users: any[] }
) & { clearTableSelection: () => void };

interface CompanyOption {
  companyId: string;
  label: string;
}

interface Props {
  target: MoveTarget | null;
  onClose: () => void;
  subdomain: string;
  notify: (message: string, variant: "success" | "error") => void;
  onError: (error: any) => void;
  onMoved?: () => void;
}

export default function MoveUserDialog(props: Props) {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const [selectedCompanyToMoveUser, setSelectedCompanyToMoveUser] =
    useState<CompanyOption | null>(null);
  const open = props.target !== null;
  const close = () => {
    setSelectedCompanyToMoveUser(null);
    props.onClose();
  };
  const cancelClose = useModalDismiss(open, close);

  const handleMoveUser = async (
    userToMove: any,
    selectedCompany: CompanyOption,
    clearTableSelection: () => void,
    skipQuestion = false
  ) => {
    const userName = userToMove.first_name + ", " + userToMove.last_name;
    const companyName = selectedCompany.label.split("(")[0];
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("confirm_move_employee_to_another_company", {
          userName,
          companyName,
        })
      );
      if (!isConfirmed) return;
    }
    const result = {
      company: selectedCompany.companyId,
      department_set: [],
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "subject",
      method: "put",
      successHandler: () => {
        props.notify(t("customer_successfully_moved"), "success");
        clearTableSelection();
        props.onMoved?.();
      },
      failHandler: (error: any) => {
        let errorMessage = error.message;
        for (const key in error.response.data) {
          const item = error.response.data[key];
          if (item !== undefined) {
            errorMessage = key + " - " + item[0];
          }
        }
        console.log(errorMessage);
        props.onError(error);
      },
      submitData: result,
      config: config,
      entityId: userToMove.person.subject,
    });
  };

  const handleBulkMoveUsersToCompany = async (
    selectedUsers: any[],
    selectedCompany: CompanyOption,
    clearTableSelection: () => void
  ) => {
    const numberOfSelectedUsers = selectedUsers.length;
    const companyName = selectedCompany.label.split("(")[0];
    const userNames = selectedUsers
      .map((user) => user.first_name + " " + user.last_name)
      .join(", ");

    const confirmMessage =
      selectedUsers.length < 10
        ? `${t("bulk_move_employees_to_another_company", {
            companyName,
            numberOfSelectedUsers,
          })} (${userNames})`
        : t("bulk_move_many_employees_to_another_company", {
            companyName,
            numberOfSelectedUsers,
          });

    const isConfirmed = await showConfirmationModalAsync(confirmMessage);

    if (!isConfirmed) return;

    try {
      const promises = selectedUsers.map((user) => {
        const result = {
          company: selectedCompany.companyId,
          department_set: [],
        };

        return new Promise<void>((resolve, reject) => {
          api.genericApiRequest({
            entity: "subject",
            method: "put",
            entityId: user.person.subject,
            submitData: result,
            successHandler: (res: any) => {
              resolve(res);
            },
            failHandler: (error: any) => reject(error),
          });
        });
      });

      await Promise.all(promises).then(() => {
        props.notify(t("customer_successfully_moved"), "success");
        clearTableSelection();
        props.onMoved?.();
      });
    } catch (error) {
      props.onError(error);
      props.notify(t("move_failed"), "error");
    }
  };

  return (
    <Dialog open={open} fullWidth={true} {...cancelClose}>
      <DialogTitle textAlign={"center"}>{t("move_customer")}</DialogTitle>
      <DialogContent>
        <FormFieldLabel label={t("company")} htmlFor="move-user-company" />
        <DropDownSubjectSearch
          name="move-user-company"
          subdomain={props.subdomain}
          onChange={(options: any) => setSelectedCompanyToMoveUser(options)}
          queryParameter="companies"
        />
        <DialogActions
          sx={{
            justifyContent: "space-between",
            padding: "0",
            marginTop: "0.5rem",
          }}
        >
          <GenericButton variant="outlined" onClick={close}>
            {t("cancel")}
          </GenericButton>

          <GenericButton
            disabled={!selectedCompanyToMoveUser?.companyId}
            onClick={() => {
              if (!props.target || !selectedCompanyToMoveUser) return;
              if (props.target.kind === "selectedUsers") {
                handleBulkMoveUsersToCompany(
                  props.target.users,
                  selectedCompanyToMoveUser,
                  props.target.clearTableSelection
                );
              } else {
                handleMoveUser(
                  props.target.user,
                  selectedCompanyToMoveUser,
                  props.target.clearTableSelection
                );
              }
              close();
            }}
          >
            {t("move")}
          </GenericButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
